import * as React from 'react'
import PropTypes from 'prop-types'
import { subDays } from 'date-fns'
import * as SubmissionClient from 'clients/min/submission'
import * as MINClient from 'clients/min'
import * as Params from '@avcan/utils/params'
import { useParams } from 'hooks/params'
import { captureException } from 'services/sentry'
import * as Async from 'contexts/async'
import * as TypesUtils from '@avcan/utils/products/min/types'

Provider.propTypes = {
    children: PropTypes.element.isRequired,
}

export function Provider({ metadata, children }) {
    const deleteSubmission = SubmissionClient.useDeleteSubmission()
    const undoDeleteSubmission = SubmissionClient.useUndoDeleteSubmission()
    const params = useParams(ParamsSchema, initialParams)
    const swr = MINClient.useSubmissions(formatParams(params.format()))

    const { data, mutate, isValidating } = swr
    const value = React.useMemo(() => {
        return {
            isValidating,
            params: params.value,
            submissions: data,
            metadata,
            paramsHaveChanged: params.haveChanged,
            changeTo(value) {
                params.merge({
                    to: value,
                    page: 1,
                })
            },
            changeFrom(value) {
                params.merge({
                    from: value,
                    page: 1,
                })
            },
            changePage(page) {
                params.merge({ page })
            },
            changeTypes(value) {
                params.merge({
                    types: value,
                    page: 1,
                })
            },
            changeRegions(value) {
                params.merge({
                    region: value,
                    page: 1,
                })
            },
            changeSorting(value) {
                params.merge({
                    sorting: value,
                    page: 1,
                })
            },
            reset: params.reset,
            async delete(id) {
                try {
                    await deleteSubmission(id)

                    mutate()
                } catch (error) {
                    captureException(error, { id })
                }
            },
            async undoDelete(id) {
                try {
                    await undoDeleteSubmission(id)

                    mutate()
                } catch (error) {
                    captureException(error, { id })
                }
            },
        }
    }, [isValidating, params, data, metadata, deleteSubmission, mutate, undoDeleteSubmission])

    return (
        <Async.Provider value={swr}>
            <context.Provider value={value}>{children}</context.Provider>
        </Async.Provider>
    )
}

export function useSubmissions() {
    const { submissions } = useContext()

    return submissions
}

export function useMetadata() {
    const { metadata } = useContext()

    return metadata
}

export function useContext() {
    return React.useContext(context)
}

// Utils and constants
const context = React.createContext()
const ParamsSchema = {
    from: Params.DateParam,
    to: Params.DateParam,
    types: Params.SetParam,
    region: Params.SetParam,
    page: Params.PageParam,
    sort: Params.SortingParam,
}
const now = new Date()
const initialParams = {
    from: subDays(now, 7),
    to: now,
}
function formatParams({ types, ...params }) {
    const { tags, obtypes } = TypesUtils.split(types)

    return {
        ...params,
        tag: Array.from(tags),
        obtype: Array.from(obtypes),
    }
}
